import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material'
import type { Supply, SupplyFormData } from '@/types'
import { SUPPLY_CATEGORIES } from '@/types'
import { useSupplyStore } from '@/store/useSupplyStore'

interface SupplyFormProps {
  open: boolean
  onClose: () => void
  supply?: Supply | null
}

export default function SupplyForm({ open, onClose, supply }: SupplyFormProps) {
  const addSupply = useSupplyStore((state) => state.addSupply)
  const updateSupply = useSupplyStore((state) => state.updateSupply)

  const [formData, setFormData] = useState<SupplyFormData>({
    name: '',
    category: 'Chemicals',
    quantity: 0,
    min_quantity: 1,
    cost: null,
    purchase_link: null,
  })

  useEffect(() => {
    if (supply) {
      setFormData({
        name: supply.name,
        category: supply.category,
        quantity: supply.quantity,
        min_quantity: supply.min_quantity,
        cost: supply.cost,
        purchase_link: supply.purchase_link,
      })
    } else {
      resetForm()
    }
  }, [supply])

  const handleChange = (field: keyof SupplyFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    if (supply) {
      await updateSupply(supply.id, formData)
    } else {
      await addSupply(formData)
    }
    onClose()
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Chemicals',
      quantity: 0,
      min_quantity: 1,
      cost: null,
      purchase_link: null,
    })
  }

  const handleClose = () => {
    onClose()
    if (!supply) resetForm()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          fontWeight: 700,
        }}
      >
        {supply ? 'Edit' : 'Add'} Supply
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2.5} mt={3}>
          <TextField
            label="Supply Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            fullWidth
            placeholder="e.g., All-Purpose Cleaner"
          />

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              label="Category"
              onChange={(e) => handleChange('category', e.target.value)}
            >
              {SUPPLY_CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Current Quantity"
            type="number"
            value={formData.quantity}
            onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0 }}
            fullWidth
          />

          <TextField
            label="Minimum Quantity (Alert Threshold)"
            type="number"
            value={formData.min_quantity}
            onChange={(e) => handleChange('min_quantity', parseInt(e.target.value) || 1)}
            inputProps={{ min: 1 }}
            fullWidth
            helperText="You'll be alerted when quantity reaches this level"
          />

          <TextField
            label="Cost (Optional)"
            type="number"
            value={formData.cost || ''}
            onChange={(e) =>
              handleChange('cost', e.target.value ? parseFloat(e.target.value) : null)
            }
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
            placeholder="0.00"
          />

          <TextField
            label="Purchase Link (Optional)"
            value={formData.purchase_link || ''}
            onChange={(e) => handleChange('purchase_link', e.target.value || null)}
            fullWidth
            placeholder="https://..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name}
          size="large"
          sx={{
            minWidth: 120,
          }}
        >
          {supply ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
